import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function LithiumCalculator({ user }) {
  // 1. State for Inputs
  const [age, setAge] = useState('0');
  const [weight, setWeight] = useState('0');
  const [scr, setScr] = useState('0');
  const [scrUnit, setScrUnit] = useState('umol') // 'umol' or 'mg/dl'
  const [gender, setGender] = useState('male');
  
  const [isMania, setIsMania] = useState(false);
  const [workflow, setWorkflow] = useState('adjustment'); // 'initial' or 'adjustment'
  // Workflow & Input States
  const [targetLevel, setTargetLevel] = useState('0.8');
  const [measuredLevel, setMeasuredLevel] = useState('0');
  const [currentDose, setCurrentDose] = useState('0');
  const [simDose, setSimDose] = useState('');
  // 2. The Handbook Formulas (The Logic)
  const genderConstant = gender === 'male' ? 1.23 : 1.04;
  const normalizedScr = scrUnit === 'mg/dl' ? scr * 88.4 : scr;

  // Calculate CrCl (Cockcroft-Gault)
  const crcl = (age > 0 && weight > 0 && scr >0) ? ((140 - age) * weight / normalizedScr) * genderConstant : 0;
  // Estimate Clearance of Lithium (mL/min)
 
  // Estimate Clearance of Lithium (L/day)
  const lithiumClearanceLday = 0.288 * crcl;
  // Mania Clearance (L/day)
  const lithiumClearanceMania = 0.432 * crcl;
  const targetRange = isMania ? "0.8 - 1.5 mmol/L" : "0.6 - 1.2 mmol/L";

  // --- WORKFLOW A: INITIAL DOSE PREDICTION ---
  // // Using your handbook formula: Css = [F * (D/T)] / CL 
  // Rearranged to find Dose per day (D/T) = (Css * CL) / F
  // Since 300mg = 8.12 mmol, we multiply by (300/8.12) to get mg
  const currentCL = isMania ? lithiumClearanceMania : lithiumClearanceLday;
  const suggestedInitialDose = currentCL > 0 
    ? Math.round((targetLevel * currentCL) * (300 / 8.12)) // 
    : 0;
  const roundedInitialDose = currentCL > 0 
    ? Math.round((targetLevel * currentCL) * (300 / 8.12)/300) * 300 // Rounded to nearest 300mg
    : 0;

    // Step A: Convert mg dose to mmol
    const doseMmol = (currentDose / 300) * 8.12;

    // Step B: Predict Css based on Step 1 CL (L/day)
    // popCL_Lday is your result from (0.25 * CrCl) + 0.1
    const predictedCss = currentCL > 0 ? (1.0 * doseMmol) / currentCL : 0;
 
    const getRecommendation = (totalDose) => {
            if (totalDose === 0) return "";
            const tabletsPerDay = totalDose / 300;
            // Scenario: Dose is 600mg
            if (tabletsPerDay === 2) return `which can be given as 300 mg BD.`;
            // Scenario: Dose is 900mg
            if (tabletsPerDay === 3) return `which can be given as 300 mg TDS.`;
            // Scenario: Dose is 1200mg
            if (tabletsPerDay === 4) return `which can be given as 600 mg BD.`;
            // Scenario: Dose is 1800mg
            if (tabletsPerDay === 6) return `which can be given as 600 mg TDS.`;
            // Fallback for other doses
            return `to be divided throughout the day (using 300 mg tablets).`;
        };
    
    // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = Number(simDose);
    const currentDoseNum = Number(currentDose) || 0;
    const measuredNum = Number(measuredLevel) || 0;
    
    let simulatedResult = 0;

    if (simDoseNum > 0 && currentDoseNum > 0 && measuredNum > 0) {
        // Direct individual patient ratio scaling matching the real biology footprint
        simulatedResult = (simDoseNum / currentDoseNum) * measuredNum;
    } else if (simDoseNum > 0 && currentCL > 0) {
        // Fallback to renal population estimate ONLY if no lab level exists yet
        const simDoseMmol = (simDoseNum / 300) * 8.12;
        simulatedResult = currentCL > 0 ? (1.0 * simDoseMmol) / currentCL : 0;
    }

    // === 3. HIGH-PRECISION VALUE DEFINITIONS (FIXES ALL NO-UNDEF ERRORS) ===
    const measured = measuredLevel > 0 ? Number(measuredLevel) : 0;
    const deviation = predictedCss > 0 ? ((measured - predictedCss) / predictedCss) * 100 : 0;

    let comparisonStatus = {
        text: "Within Range",
        color: "#2ecc71",
        note: "Patient is responding as expected for an average adult/pediatric profile."
    };

    if (measured < predictedCss * 0.8) {
        comparisonStatus = {
            text: "Below Expectation",
            color: "#3498db",
            note: `Actual level is ${Math.abs(deviation).toFixed(0)}% lower than predicted. Check for non-compliance, malabsorption, or early sampling.`
        };
    } else if (measured > predictedCss * 1.2) {
        comparisonStatus = {
            text: "Above Expectation",
            color: "#e67e22",
            note: `Actual level is ${deviation.toFixed(0)}% higher than predicted. Check for renal/hepatic impairment or enzyme inhibition.`
        };
    }

    let liSuggestion = "";
        let liColor = "#2ecc71";

        if (measuredLevel < 0.6) {
            liColor = "#f1c40f";
            liSuggestion = "Level is sub-therapeutic (< 0.6 mmol/L). If response is poor, Check compliance first before consider a dose increment. If response is poor but compliance is good, increase dose and re-sample. If response is good, continue current dose.";
        } else if (measuredLevel >= 0.6 && measuredLevel <= 1.2) {
            liColor = "#2ecc71";
            liSuggestion = "Level within therapeutic range (0.6-1.2 mmol/L). If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";
        } else if (measuredLevel > 1.2) {
            liColor = "#e74c3c";
            liSuggestion = "Level is TOXIC (> 1.2 mmol/L). Withhold dose. Monitor for tremors, ataxia, or confusion. Review dose once level falls.";
        }

    const getLithiumInsight = () => {
        if (!measuredLevel || !predictedCss) return null;
        
        const actual = Number(measuredLevel);
        const estimate = Number(predictedCss);

        if (actual > estimate * 1.2) {
            return {
                title: "Actual > Population Estimate",
                reason: "Potential increase concentration/effects of lithium by Thiazides, ACEi & ARB interaction appears to be delayed (3-5 weeks), Non-Steroidal Anti-Inflammatory Drug (except ASPIRN), Spironolactone, Calcium Channel Blockers (CCBs), Selective Serotonin Reuptake Inhibitor (SSRI): fluoxetine, sertraline, fluvoxamine, Frusemide, Amiloride.",
                color: "#e67e22" // Orange
            };
        } else if (actual < estimate * 0.8) {
            return {
                title: "Actual < Population Estimate",
                reason: "Potential decrease concentration/effects of lithium by Osmotic diuretics (Mannitol), Methylxanthines (Theophylline, Caffeine), Carbonic anhydrase inhibitors (acetazolamide).",
                color: "#3498db" // Blue
            };
        }
        return {
            title: "Level Matches Estimate",
            reason: "The current blood level is consistent with the patient's calculated renal clearance.",
            color: "#2ecc71" // Green
        };
    };
    const insight = getLithiumInsight();

  return (
    
    <div style={{ background: '#1b1f23', padding: '20px', borderRadius: '10px', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
            type="button"
            className="no-print" 
            onClick={() => {
                const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                const trackingLabel = `${actualUserEmail}-printed-Lithium`;
                logUserActivityQuietly(trackingLabel); 
                window.print();
            }}
            style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
            }}
        >
            🖨️ Print PDF Report
        </button>
        </div>
        <h2 style={{ color: '#3498db', marginTop: 0 , textAlign: 'center'}}>Lithium TDM Worksheet</h2>
        <details style={{
            backgroundColor: '#1E293B',
            border: '1px solid #38BDF8',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '20px',
            cursor: 'pointer'
          }}>
            <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
              ⏱️ Check Clinical Sampling Protocol (Click to expand)
            </summary>
            <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                {/* SECTION 1: THE CRITICAL 12-HOUR SAMPLING WINDOW */}
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                        🚨 Crucial 12-Hour Distribution Window
                    </span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>Steady State Timeline:</td>
                                <td style={{ padding: '6px 0', color: '#bdc3c7' }}>Draw steady-state serum levels after **4 – 5 days** of initiation/change.</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#00d2ff', verticalAlign: 'top' }}>Standard Standard:</td>
                                <td style={{ padding: '6px 0', color: '#fff', lineHeight: '1.4' }}>
                                    🛡️ **Must be drawn exactly 12 hours from the last evening dose** (Pre: right before the next morning dose). Early collection causes false toxic spikes.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* SECTION 2: THERAPEUTIC INDEX MATRIX GRID */}
                <div style={{ marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2ecc71', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                        🎯 Indication & Age Target Windows
                    </span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {/* Standard Indications */}
                        <div style={{ background: 'rgba(46, 204, 113, 0.04)', border: '1px solid rgba(46, 204, 113, 0.15)', padding: '10px', borderRadius: '6px' }}>
                            <div style={{ fontSize: '0.8rem', color: '#bdc3c7', fontWeight: 'bold' }}>🔥 ACUTE MANIA:</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff', margin: '2px 0' }}>0.8 - 1.5 <span style={{fontSize:'0.75rem', fontWeight:'normal'}}>mmol/L</span></div>
                            <div style={{ fontSize: '0.8rem', color: '#bdc3c7', fontWeight: 'bold', marginTop: '8px' }}>💤 MAINTENANCE DOSE:</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff', margin: '2px 0' }}>0.6 - 1.2 <span style={{fontSize:'0.75rem', fontWeight:'normal'}}>mmol/L</span></div>
                        </div>

                        {/* Geriatric ISBD CPG Guidelines */}
                        <div style={{ background: 'rgba(241, 196, 15, 0.04)', border: '1px solid rgba(241, 196, 15, 0.15)', padding: '10px', borderRadius: '6px', fontSize: '0.8rem' }}>
                            <div style={{ fontWeight: 'bold', color: '#f1c40f', marginBottom: '4px' }}>👵 Geriatric Bipolar (ISBD CPG):</div>
                            <div style={{ color: '#bdc3c7', lineHeight: '1.4' }}>
                            • **60 – 79 Years Old:** 0.4 - 0.8 mmol/L<br />
                            • **80 Years & Older:** 0.4 - 0.7 mmol/L<br />
                            <span style={{ fontStyle: 'italic', color: '#95a5a6', display: 'block', marginTop: '4px' }}>*Elderly patients require significantly lower targets to minimize risk of neurotoxicity.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECTION 3: STRATIFIED TOXICITY WARNING LADDER */}
                <div style={{ background: 'rgba(231, 76, 60, 0.06)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                        ⚠️ Stratified Toxicity Risk Scale
                    </span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8rem', marginTop: '6px', color: '#bdc3c7' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#ff7675', width: '30%' }}>Mild Toxicity:</td>
                                <td style={{ padding: '4px 0' }}>**1.5 – 2.0 mmol/L** (Fine hand tremors, nausea, diarrhea, polyuria)</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#ff7675' }}>Moderate Toxicity:</td>
                                <td style={{ padding: '4px 0' }}>**2.0 – 2.5 mmol/L** (Course tremors, ataxia, slurred speech, confusion)</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#e74c3c' }}>Severe Toxicity:</td>
                                <td style={{ padding: '4px 0', color: '#fff' }}>🔥 **&gt; 2.5 mmol/L** (Seizures, cardiac arrhythmias, renal failure, coma)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

            </div>
        </details>
        {/* SECTION 1: PATIENT PARAMETERS */}
        <h4 style={{color: '#bdc3c7', marginBottom: '10px'}}>Step 1: Patient Data (Cockcroft-Gault)</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', padding: '15px', background: 'rgba(0,0,0,0.1)', borderRadius: '8px' }}>
            <div>
                <label>Age:</label>
                <input type="number" value={age} onChange={(e) => setAge(e.target.value)} style={inputStyle} />
            </div>
            <div>
                <label>Weight (kg):</label>
                <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
            </div>
            <div>
                <label>SCr {scrUnit}:</label>
                <input type="number" value={scr} onChange={(e) => setScr(e.target.value)} style={inputStyle} />
                <select value={scrUnit} onChange={(e) => setScrUnit(e.target.value)}style={{ width: '35%' }}>
                      <option value="umol">µmol/L</option>
                      <option value="mg/dl">mg/dL</option>
                </select>   
            </div>
            <div>
                <label>Gender:</label>
                <button onClick={() => setGender(gender === 'male' ? 'female' : 'male')} 
                    style={{ ...inputStyle, background: '#3498db', cursor: 'pointer', fontWeight: 'bold' }}>
                    {gender.toUpperCase()}
                </button>
            </div>
        </div>
       
        {/* SECTION 2: DOSING & STATUS */}
        <div style={{ textAlign: 'center' }}>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Patient Condition:</label>
            <button onClick={() => setIsMania(!isMania)} style={btnStyle(isMania)}>
                {isMania ? "Acute Mania" : "Stable/Maintenance"}
            </button>
        </div>
        {/* Results Display */}
        <div style={{ marginTop: '25px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div style={{ background: '#34495e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#bdc3c7' }}>Calculated Renal Function:</p>
                <div>CrCl (mL/min) = ((140 - {age}) * {weight} / {Number(normalizedScr).toFixed(1)})* {genderConstant} </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f1c40f' }}>{crcl.toFixed(1)}</div>
            </div>
            <div style={{ background: '#34495e', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid #f1c40f' }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#bdc3c7' }}>Li Clearance (L/day):</p>
                <div> {isMania ? `CL (L/d) = 0.432 (${crcl.toFixed(1)})`: `CL (L/d) = 0.288 (${crcl.toFixed(1)})`}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f1c40f' }}>
                    {isMania ? lithiumClearanceMania.toFixed(2) : lithiumClearanceLday.toFixed(2)}
                </div>
            </div>
        </div>
        <div style={{ background: '#2c3e50', padding: '25px', borderRadius: '10px', border: '2px solid #f1c40f', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.1rem', color: '#f1c40f', margin: 0 }}>Step 2: Dosing & TDM Adjustment</h3>
                    {/* Workflow Toggle */}
                <div style={{ background: '#34495e', padding: '5px', borderRadius: '8px' }}>
                    <button 
                        onClick={() => setWorkflow('initial')} 
                        style={workflow === 'initial' ? activeTabStyle : inactiveTabStyle}>
                        New Patient
                    </button>
                    <button 
                        onClick={() => setWorkflow('adjustment')} 
                        style={workflow === 'adjustment' ? activeTabStyle : inactiveTabStyle}>
                        TDM Adjustment
                    </button>
                </div>
            </div>
            {workflow === 'initial' ? (
                /* WORKFLOW A: STARTING DOSE (Predictive) */
                <div>
                    <p style={{ fontSize: '0.85rem', color: '#bdc3c7' }}>Predicting dose based on Renal Clearance:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={{ fontSize: '0.8rem' }}>Target Level (mmol/L):</label>
                            <input type="number" value={targetLevel} onChange={(e) => setTargetLevel(e.target.value)} style={inputStyle} />
                            <p style={{ fontSize: '0.7rem', color: '#f1c40f' }}>Recommended Target for this condition: {targetRange}</p>
                        </div>
            
                        <div style={{ textAlign: 'center', background: '#1e272e', padding: '15px', borderRadius: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#bdc3c7' }}>Suggested Starting Dose:</span>
                            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f1c40f' }}>
                                {suggestedInitialDose} <span style={{ fontSize: '0.9rem' }}>mg/day</span>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#bdc3c7' }}>InitialDose = ((({targetLevel} * {currentCL.toFixed(2)}) * (300 / 8.12)))</p>
                        </div>
                    </div>
                    <div style={{ marginTop: '20px', textAlign: 'center', background: '#1e272e', padding: '15px', borderRadius: '8px', border: '1px solid #2ecc71' }}>
                            <span style={{ fontSize: '0.75rem', color: '#bdc3c7' }}>Rounded Starting Dose:</span>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2ecc71' }}>
                                {roundedInitialDose} <span style={{ fontSize: '1rem' }}>mg/day</span>
                            </div>
                            <div style={{ fontSize: '1.1rem', color: '#ecf0f1', fontStyle: 'italic', borderTop: '1px solid #2ecc71', paddingTop: '10px' }}>
                                "{getRecommendation(roundedInitialDose)}"
                            </div>
                    </div>
                </div>
            ) : (
                /* WORKFLOW B: TDM ADJUSTMENT (Ratio Method) */
                <div>
                    <p style={{ fontSize: '0.85rem', color: '#bdc3c7' }}>Adjusting based on measured blood level:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        {/* Input 1: Current Dose */}
                        <div>
                            <label style={{ fontSize: '0.8rem', color: '#ecf0f1' }}>Current Dose (mg/day):</label>
                            <input 
                                type="number" 
                                value={currentDose} 
                                onChange={(e) => {const val = e.target.value; setCurrentDose(val === "" ? "" : Number(val))}}
                                style={inputStyle} 
                            />
                        </div>
                        {/* Input 2: Measured Level */}
                        <div>
                            <label style={{ fontSize: '0.8rem' }}>Measured Level (mmol/L):</label>
                            <input type="number" value={measuredLevel} onChange={(e) => setMeasuredLevel(e.target.value)} style={inputStyle} />
                        </div>
                        <div></div>
                        <div>
                            Recommended Target for this condition: 0.6 - 1.2 mmol/L
                        </div>
                    </div>
                    <div style={{ padding: '10px', background: '#34495e', borderRadius: '5px', marginBottom: '10px' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#bdc3c7' }}>
                            <strong>Conversion Logic:</strong>
                        </p>
                        <code style={{ color: '#f1c40f' }}>
                            Dose (mmol) = ({currentDose} mg / 300 mg) × 8.12 mmol = {doseMmol.toFixed(2)} mmol
                        </code>
                    </div>
                    <div style={{ padding: '10px', background: '#34495e', borderRadius: '5px', marginBottom: '10px' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#bdc3c7' }}>
                            <strong>Predicted Steady-State (Css):</strong>
                        </p>
                        <code style={{ color: '#f1c40f' }}>
                            Css (mmol/L) = [F × (Dose / Interval)] / CL <br />
                            [1.0 × ({doseMmol.toFixed(2)} mmol / 1 day)] / {currentCL.toFixed(2)} L/day = 
                            <strong> {predictedCss.toFixed(2)} mmol/L</strong>
                        </code>
                    </div>
                    <div style={{ marginTop: '20px', padding: '15px', background: '#1e272e', borderRadius: '8px' }}>
                        <h4 style={{ margin: 0, color: liColor, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                            Step 3: Clinical Correlation
                        </h4>
                        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '6px', marginBottom: '15px' }}>
                            <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#bdc3c7' }}>Current Status:</p>
                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                Actual Level: {measuredLevel} mcg/mL
                            </div>
                            <div>
                                Predicted Steady State (Cₚₛₛ): <span style={{color: '#f18b0f'}}>{Number(predictedCss).toFixed(2)} mcg/mL</span>
                            </div>
                        </div>
                        {measured > 0 && (
                        
                            <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', borderLeft: `6px solid ${comparisonStatus.color}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span style={{ fontWeight: 'bold', color: comparisonStatus.color }}>{comparisonStatus.text}</span>
                                    <span style={{ fontSize: '0.8rem', color: '#95a5a6'}}>Dev: {deviation > 0 ? '+' : ''}{deviation.toFixed(1)}%</span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                                    {comparisonStatus.note}
                                </p>
                            </div>
                        )}
                    </div>
                    {/* STEP 4: DOSE SIMULATION (INDIVIDUAL HIGH-PRECISION PATIENT MODEL) */}
                    <h4 style={{ margin: '0 0 10px 0', color: '#3498db', fontSize: '0.9rem', marginTop: '20px' }}>
                        Step 4: Dose Simulation
                    </h4>
                    
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.7rem', color: '#95a5a6', display: 'block', marginBottom: '4px' }}>NEW PROPOSED DOSE (mg/day)</label>
                            <input 
                                type="number" 
                                value={simDose}
                                onChange={(e) => setSimDose(e.target.value)}
                                placeholder="e.g. 900"
                                style={{ width: '100%', padding: '8px', background: '#1b1f23', border: simDose ? '1px solid #3498db' : '1px solid #34495e', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }}
                                disabled={!measuredLevel || !currentDose}
                            />
                        </div>
                        
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>EST. NEW CSS</div>
                            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#00d2ff' }}>
                                {simulatedResult ? simulatedResult.toFixed(2) : '--'} <span style={{ fontSize: '0.7rem', fontWeight: 'normal', color: '#95a5a6' }}>mmol/L</span>
                            </div>
                        </div>
                    </div>

                    {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
                    {simulatedResult > 0 && (
                        <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '15px' }}>
                            <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                                
                                {/* DYNAMIC TARGET BOUNDARY HIGHLIGHT SHADOW (Shifts based on isMania configuration state) */}
                                {/* Plotted context landscape across an absolute x-axis cap of 2.5 mmol/L */}
                                <div style={{ 
                                    position: 'absolute', 
                                    left: isMania ? '32%' : '24%', /* 0.8 mmol/L is 32% of 2.5 | 0.6 mmol/L is 24% of 2.5 */
                                    width: isMania ? '28%' : '24%', /* (1.5-0.8)/2.5 = 28% span | (1.2-0.6)/2.5 = 24% span */
                                    height: '100%', 
                                    background: 'rgba(46, 204, 113, 0.22)', 
                                    borderLeft: '1px dashed #2ecc71', 
                                    borderRight: '1px dashed #2ecc71',
                                    borderRadius: '4px'
                                }} />

                                {/* ACTIVE PATIENT PINPOINT INDICATOR NODE (Glowing yellow indicator bead node) */}
                                <div style={{
                                    position: 'absolute',
                                    left: `${Math.max(0, Math.min(98, (simulatedResult / 2.5) * 100))}%`,
                                    transform: 'translateX(-50%)',
                                    width: '18px',
                                    height: '18px',
                                    top: '-2px',
                                    background: '#f1c40f',
                                    borderRadius: '50%',
                                    boxShadow: '0 0 12px #f1c40f',
                                    border: '2px solid #fff',
                                    transition: 'left 0.2s ease-out',
                                    zIndex: 10
                                }} />
                            </div>

                            {/* GRAPH SCALING X-AXIS SCALE FOOTER LABELS */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                                <span>0 mmol/L</span>
                                <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Strategy Window: {targetRange}</span>
                                <span>2.5 mmol/L</span>
                            </div>

                            {/* CLEAR LINEAR RATIO CLINICAL AUDIT TRAIL TRACE PRINT */}
                            <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                                {measuredNum > 0 ? (
                                    <>
                                        Formula Predict Css: (Proposed New Dose / Current Daily Dose) × Actual Measured Lab Level<br />
                                        Individual Patient Linear Ratio Trace: ({simDose || 0} mg / {currentDose} mg) × {measuredLevel} mmol/L = {simulatedResult.toFixed(2)} mmol/L
                                    </>
                                ) : (
                                    <>
                                        Formula Predict Css: [Bioavailability × Dose (mmol)] / Systemic Clearance Value (L/day)<br />
                                        Population Renal Estimate Reference Trace: (1.0 × {((simDoseNum / 300) * 8.12).toFixed(2)} mmol) / {currentCL.toFixed(2)} L/day
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CLINICAL SUMMARY ACTION BANNER PANEL */}
                    <div style={{ margin: '15px 0', padding: '15px', background: '#1b1f23', borderRadius: '8px', borderLeft: `6px solid ${liColor}`, boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}>
                        <strong style={{ color: liColor, fontSize: '0.85rem', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Recommendation:</strong>
                        <p style={{ margin: '0', lineHeight: '1.4', fontSize: '0.88rem', color: '#ecf0f1' }}>
                            {liSuggestion} {simulatedResult > 0 && `Your simulated change predicts a steady-state level of ${simulatedResult.toFixed(2)} mmol/L.`}
                        </p>
                        <p style={{ fontSize: '0.72rem', color: '#95a5a6', marginTop: '8px', fontStyle: 'italic', margin: '8px 0 0 0' }}>
                            *Lithium clearance is highly sensitive to sodium intake, fluid shifts, and hydration patterns. Ensure stable hydration.
                        </p>
                    </div>

                    {/* INTERPRETATION AUDIT MATRIX BOX */}
                    {insight && (
                        <div style={{ 
                            marginTop: '15px', 
                            padding: '12px', 
                            background: '#1b1f23', 
                            borderRadius: '8px', 
                            borderLeft: `5px solid ${insight.color}`,
                            marginBottom: '15px'
                        }}>
                            <h5 style={{ margin: '0 0 5px 0', color: insight.color, fontSize: '0.9rem' }}>
                                🔍 Interpretation: {insight.title}
                            </h5>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                                {insight.reason}
                            </p>
                        </div>
                    )}

                </div>
            )}
            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </div>
    </div>
  );
};
// Simple styles to keep the code clean
const inputStyle = { width: '90%', padding: '8px', background: '#1c2833', border: '1px solid #34495e', color: 'white', borderRadius: '4px' };
const btnStyle = (active) => ({ padding: '8px 12px', border: 'none', borderRadius: '4px', background: active ? '#f1c40f' : '#3498db', color: 'white', cursor: 'pointer' });
const activeTabStyle = {
    padding: '8px 15px',
    border: 'none',
    borderRadius: '6px',
    background: '#f1c40f',
    color: '#2c3e50',
    fontWeight: 'bold',
    cursor: 'pointer'
};
const inactiveTabStyle = {
    padding: '8px 15px',
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    color: '#bdc3c7',
    cursor: 'pointer'
};
export default LithiumCalculator;